/**
 * Module-level mirror of the active "operate as tenant" scope.
 *
 * The {@link CompanyScopeProvider} (React) owns the picker UI + companies list,
 * but many data fetchers here run OUTSIDE React (the Refine `dataProvider`, the
 * Batch/Live-feed module fetchers). They need the current scope synchronously at
 * call time, so the provider mirrors every selection into this module and these
 * fetchers read it via {@link getScopeCompanyId}.
 *
 * `null` = "All tenants": no `?companyId=` is appended → the backend returns the
 * cross-tenant union (the historical default). A non-null value scopes every
 * scope-aware read to that one tenant.
 *
 * The choice is persisted to localStorage and restored on import, so the module
 * scope is already correct before the first data call fires (the provider mounts
 * above `<Refine>`, but this import-time read guards even earlier callers).
 */

const STORAGE_KEY = 'superadmin.scope.companyId';

function readPersisted(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage can throw in private-mode / SSR-ish contexts — treat as none.
    return null;
  }
}

let scopeCompanyId: string | null = readPersisted();

/** The active tenant scope, or `null` for "All tenants" (cross-tenant union). */
export function getScopeCompanyId(): string | null {
  return scopeCompanyId;
}

/**
 * Set the active tenant scope and persist it. Pass `null` to clear (All tenants).
 * Called by {@link CompanyScopeProvider} whenever the picker selection changes.
 */
export function setScopeCompanyId(id: string | null): void {
  scopeCompanyId = id;
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Persistence is best-effort; a storage failure must not break scoping.
  }
}
